import React, { useState, useEffect } from 'react';
import EditableSection from 'components/v2/EditableSection';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import Select from 'components/v2/select';
import { useTranslation } from 'react-i18next';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import { checkPermissonForEditShipperBillingModule } from './utils';
const monthOptions = [
    {
        value: '1',
        name: 'Jan',
        localizationSlug: 'JAN',
    },
    {
        value: '2',
        name: 'Feb',
        localizationSlug: 'FEB',
    },
    {
        value: '3',
        name: 'Mar',
        localizationSlug: 'MAR',
    },
    {
        value: '4',
        name: 'Apr',
        localizationSlug: 'APR',
    },
    {
        value: '5',
        name: 'May',
        localizationSlug: 'MAY',
    },
    {
        value: '6',
        name: 'Jun',
        localizationSlug: 'JUN',
    },
    {
        value: '7',
        name: 'Jul',
        localizationSlug: 'JUL',
    },
    {
        value: '8',
        name: 'Aug',
        localizationSlug: 'AUG',
    },
    {
        value: '9',
        name: 'Sep',
        localizationSlug: 'SEP',
    },
    {
        value: '10',
        name: 'Oct',
        localizationSlug: 'OCT',
    },
    {
        value: '11',
        name: 'Nov',
        localizationSlug: 'NOV',
    },
    {
        value: '12',
        name: 'Dec',
        localizationSlug: 'DEC',
    },
];
export const CreditCardSummary = ({ fields }) => {
    const { t } = useTranslation();
    const formatCardNumber = (data = {}) => {
        const { last4, brand } = data;
        if (last4 && brand) {
            const currentCard = t('BRAND_ENDING_IN', {
                brand,
                last4,
            });
            return currentCard;
        }
        return '';
    };
    const formatCardExpiry = (data = {}) => {
        const { expiry_month, expiry_year } = data;
        if (expiry_month && expiry_year) {
            const cardExpiryDate = t('EXPIRES_ON', {
                expiry_month: expiry_month.toString().padStart(2, '0'),
                expiry_year,
            });
            return cardExpiryDate;
        }
        return '';
    };
    const cardNumber = formatCardNumber(fields);
    const cardExpiryDate = formatCardExpiry(fields);
    return (React.createElement("div", { className: "grid grid-cols-2 gap-4" },
        React.createElement(DescriptionListItem, { label: t('STRIPE_ID') }, fields.stripeId ? fields.stripeId : t('NOT_SET')),
        React.createElement(DescriptionListItem, { label: t('CARD') },
            cardNumber || t('NO_CC_REGISTERED'),
            " ",
            React.createElement("br", null),
            cardExpiryDate || '')));
};
export const CreditCardForm = ({ fields, onChange, saving, onSaveShipper, onSaveSuccess, onSaveFail, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { t } = useTranslation();
    const requiredFields = ['number', 'expiry_month', 'expiry_year', 'cvv'];
    const validate = () => {
        const error = {};
        requiredFields.forEach((key) => {
            if (!fields[key]) {
                error[key] = t('FIELD_IS_REQUIRED', { field: t(key.toUpperCase()) });
            }
        });
        onChange('error', error);
        if (Object.keys(error).length) {
            return false;
        }
        return true;
    };
    const saveForm = async () => {
        try {
            if (validate()) {
                await onSaveShipper();
                onSaveSuccess(fields);
            }
        }
        catch (e) {
            onSaveFail();
        }
    };
    useEffect(() => {
        if (saving) {
            saveForm();
        }
    }, [saving]);
    return (React.createElement("div", { className: "grid grid-cols-2 lg:grid-cols-3 gap-4" },
        React.createElement("div", { className: "col-span-full" },
            React.createElement(Label, null, t('NUMBER')),
            React.createElement(TextField, { className: "mt-1", placeholder: t('CREDIT_CARD_NUMBER'), value: fields.number, onChange: (val) => {
                    onChange('number', val);
                } }),
            !fields.number && ((_a = fields === null || fields === void 0 ? void 0 : fields.error) === null || _a === void 0 ? void 0 : _a.number) && (React.createElement(ErrorLabel, { className: "mt-1" }, (_b = fields === null || fields === void 0 ? void 0 : fields.error) === null || _b === void 0 ? void 0 : _b.number))),
        React.createElement("div", { className: "" },
            React.createElement(Label, null, t('EXPIRY_MONTH')),
            React.createElement(Select, { labelClassName: "m-0", items: monthOptions, textKey: "localizationSlug", valueKey: "value", selected: fields === null || fields === void 0 ? void 0 : fields.expiry_month, onSelect: (option) => onChange('expiry_month', option === null || option === void 0 ? void 0 : option.value) }),
            !fields.expiry_month && ((_c = fields === null || fields === void 0 ? void 0 : fields.error) === null || _c === void 0 ? void 0 : _c.expiry_month) && (React.createElement(ErrorLabel, { className: "mt-1" }, (_d = fields === null || fields === void 0 ? void 0 : fields.error) === null || _d === void 0 ? void 0 : _d.expiry_month))),
        React.createElement("div", { className: "" },
            React.createElement(Label, null, t('EXPIRY_YEAR')),
            React.createElement(TextField, { className: "mt-1", placeholder: t('EXPIRY_YEAR'), value: fields.expiry_year, onChange: (val) => onChange('expiry_year', val) }),
            !fields.expiry_year && ((_e = fields === null || fields === void 0 ? void 0 : fields.error) === null || _e === void 0 ? void 0 : _e.expiry_year) && (React.createElement(ErrorLabel, { className: "mt-1" }, (_f = fields === null || fields === void 0 ? void 0 : fields.error) === null || _f === void 0 ? void 0 : _f.expiry_year))),
        React.createElement("div", { className: "" },
            React.createElement(Label, null, t('CVV')),
            React.createElement(TextField, { className: "mt-1", placeholder: t('CVV'), value: fields.cvv, onChange: (val) => onChange('cvv', val) }),
            !fields.cvv && ((_g = fields === null || fields === void 0 ? void 0 : fields.error) === null || _g === void 0 ? void 0 : _g.cvv) && (React.createElement(ErrorLabel, { className: "mt-1" }, (_h = fields === null || fields === void 0 ? void 0 : fields.error) === null || _h === void 0 ? void 0 : _h.cvv)))));
};
const CreditCard = ({ data, className, onUpdateShipper }) => {
    const { t } = useTranslation();
    const [initialData, setInitialData] = useState(data);
    const [creditCardData, setCreditCardData] = useState(data);
    const getInitData = () => {
        if (data) {
            setInitialData(data);
            setCreditCardData(data);
        }
    };
    const handleSave = async () => {
        const updates = Object.assign({}, creditCardData);
        if (updates.expiry_month) {
            updates.expiry_month = parseInt(updates.expiry_month, 10);
        }
        await onUpdateShipper(updates);
    };
    const onChange = (field, value) => {
        setCreditCardData((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
    };
    const handleAfterSave = (onSaveSuccess) => {
        return (updatedData = {}) => {
            setInitialData((prev) => (Object.assign(Object.assign({}, prev), updatedData)));
            onSaveSuccess();
        };
    };
    useEffect(() => {
        getInitData();
    }, [data]);
    return (React.createElement(EditableSection, { title: t('CREDIT_CARD'), canEdit: checkPermissonForEditShipperBillingModule(), className: className, onSave: handleSave }, (editMode, saving, onSaveSuccess, onSaveFail) => editMode ? (React.createElement(CreditCardForm, { fields: creditCardData, onChange: onChange, saving: saving, onSaveSuccess: handleAfterSave(onSaveSuccess), onSaveFail: () => onSaveFail(), onSaveShipper: () => handleSave() })) : (React.createElement(CreditCardSummary, { fields: initialData }))));
};
export default CreditCard;
